import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { LocaleCode } from '@/api/models';
import type { ErrorResponse } from '@/api/handlers/responses';
import {
    useOperationCatalogQuery,
    useSetOperationBlockedMutation,
} from '@/api/queries';
import { PATH } from '@/router/PATH';
import {
    resolveLocale,
    unwrapStatement,
} from '@/pages/accounting/statements/statementMeta';
import { getAuthMethod, getUser } from '@/utils/authSession';
import {
    canEditOperations,
    canReadOperations,
    deriveOperationsAccess,
    type OperationsAccess,
} from '../operationsAccess';
import {
    emptyStateFor,
    matchesSearch,
    matchesStatus,
    toOperationRow,
    type OperationRow,
    type StatusFilter,
    type TemplatesEmptyState,
} from './templatesMeta';

export type UseTemplatesPageResult = {
    access: OperationsAccess;
    /** Whether the catalog is readable at all — the page renders a notice if not. */
    canRead: boolean;
    locale: LocaleCode;
    isLoading: boolean;
    error: ErrorResponse | null;
    onRetry: () => void;
    /** The rows the filters leave, or `null` until the first load resolves. */
    rows: OperationRow[] | null;
    /** How many rows the catalog holds before filtering — for the count label. */
    totalCount: number;
    search: string;
    onSearchChange: (value: string) => void;
    statusFilter: StatusFilter;
    onStatusFilterChange: (filter: StatusFilter) => void;
    /** Why the table is empty, if it is. Never `NO_MATCH` while loading. */
    emptyState: TemplatesEmptyState;
    /** Whether this session may change a template — the modal's Edit button reads it. */
    canEdit: boolean;
    /** Open the editor for a template. */
    onEdit: (key: string) => void;
    /**
     * The row whose recipe the preview modal is showing, `null` when closed.
     *
     * The **row**, not the key: the modal's header names the operation before
     * its own read resolves, and the catalog already holds both names.
     */
    previewRow: OperationRow | null;
    onPreview: (key: string) => void;
    onPreviewOpenChange: (open: boolean) => void;
    /** Open the version history. Offered on every template, whatever the role. */
    onOpenHistory: (key: string) => void;
    /**
     * The template whose history drawer is open, `null` when it is closed —
     * read from `?history=<key>`, so the link survives a refresh and can be
     * pasted into a support thread.
     */
    historyKey: string | null;
    onCloseHistory: () => void;
    /**
     * Retire an operation or bring one back, straight from the row. `null` for a
     * session that may not change anything — the affordance is then not rendered
     * at all rather than rendered disabled.
     */
    onSetBlocked: ((key: string, blocked: boolean) => void) | null;
    /**
     * Open the create form. `null` for a session that may not change anything —
     * the same "absent, not disabled" rule as `onSetBlocked`.
     */
    onCreate: (() => void) | null;
    /**
     * Open the category management screen.
     *
     * Not `null` for a read-only session, unlike the two above: this is
     * navigation to a screen that renders itself read-only, not a mutation. A
     * BranchAdmin who can see the chips can see where they come from.
     */
    onManageCategories: () => void;
};

/**
 * Drives the Templates tab.
 *
 * One request, no parameters. Searching and filtering happen here over the
 * loaded list rather than as query parameters, because the catalog is seventeen
 * rows on a fresh tenant — a round trip per keystroke would be slower and would
 * make the status filter's interaction with the system rows a server concern.
 *
 * **Access is derived before fetching.** A PIN/kiosk token is refused by every
 * accounting endpoint whatever role it carries, so a page that fetches in order
 * to find that out shows a failure where the truth is a permission, and a raw
 * 403 is not something anyone can act on. `canRead` gates the query's `enabled`.
 */
export const useTemplatesPage = (): UseTemplatesPageResult => {
    const { i18n } = useTranslation('pages');
    const locale = resolveLocale(i18n.language);

    // Read once per mount, like the other accounting screens: the session cannot
    // change without a navigation that remounts this page.
    const access = React.useMemo(
        () => deriveOperationsAccess(getUser(), getAuthMethod()),
        [],
    );
    const canRead = canReadOperations(access);
    const canEdit = canEditOperations(access);

    const [search, setSearch] = React.useState('');
    const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('ALL');
    const [previewKey, setPreviewKey] = React.useState<string | null>(null);

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const setBlocked = useSetOperationBlockedMutation();

    const query = useOperationCatalogQuery(canRead);
    const { data, error } = unwrapStatement(query.data, query.error);

    const allRows = React.useMemo(
        () =>
            data ? data.rows.map((row) => toOperationRow(row, canEdit)) : null,
        [data, canEdit],
    );

    const rows = React.useMemo(
        () =>
            allRows?.filter(
                (row) =>
                    matchesSearch(row, search) &&
                    matchesStatus(row, statusFilter),
            ) ?? null,
        [allRows, search, statusFilter],
    );

    const onRetry = React.useCallback(() => {
        query.refetch();
    }, [query]);

    const onEdit = React.useCallback(
        (key: string) => {
            navigate(PATH.ACCOUNTING.OPERATIONS.BIND_EDIT(key));
        },
        [navigate],
    );

    const onCreate = React.useCallback(() => {
        navigate(PATH.ACCOUNTING.OPERATIONS.NEW);
    }, [navigate]);

    const onManageCategories = React.useCallback(() => {
        navigate(PATH.ACCOUNTING.OPERATIONS.CATEGORIES);
    }, [navigate]);

    const onPreview = React.useCallback((key: string) => {
        setPreviewKey(key);
    }, []);

    const onPreviewOpenChange = React.useCallback((open: boolean) => {
        if (!open) {
            setPreviewKey(null);
        }
    }, []);

    /**
     * Open and close the history drawer through the URL.
     *
     * **Opening pushes, closing replaces.** The push is what makes the browser's
     * back button close the drawer, which is the gesture a reader who opened it
     * from a row will reach for first; replacing on the way out keeps a repeated
     * open/close from burying the catalog under a stack of its own states.
     */
    const onOpenHistory = React.useCallback(
        (key: string) => {
            setSearchParams((current) => {
                const next = new URLSearchParams(current);
                next.set('history', key);
                return next;
            });
        },
        [setSearchParams],
    );

    const onCloseHistory = React.useCallback(() => {
        setSearchParams(
            (current) => {
                const next = new URLSearchParams(current);
                next.delete('history');
                return next;
            },
            { replace: true },
        );
    }, [setSearchParams]);

    /**
     * Block or unblock from the row.
     *
     * Prospective either way — the operation stops being offered for recording
     * and every entry it has already posted is untouched — which is why this is
     * a one-press action here rather than a confirm dialog. The editor, where an
     * author is looking at the recipe itself, is where the rule is spelled out.
     * A refusal falls into the catalog's own error state via the invalidated
     * query rather than being surfaced per row: the only refusal this call can
     * collect is `STATUS_UNCHANGED`, which means the row already shows the
     * answer.
     */
    const onSetBlocked = React.useCallback(
        (key: string, blocked: boolean) => {
            setBlocked.mutate({ key, blocked });
        },
        [setBlocked],
    );

    // `rows === null` is "not known yet", and must never render an empty state:
    // announcing "no operations" over a catalog that is still loading is the
    // confident falsehood this folder exists to avoid.
    const emptyState: TemplatesEmptyState =
        allRows === null || rows === null
            ? 'NONE'
            : emptyStateFor(allRows.length, rows.length);

    // Resolved against the **unfiltered** rows: a search typed while the modal
    // is open must not empty the modal behind it.
    const previewRow =
        allRows?.find(
            (row) => row.key === previewKey && row.kind === 'TEMPLATE',
        ) ?? null;

    return {
        access,
        canRead,
        locale,
        canEdit,
        // A session that may not read is not loading — it is answered. Leaving
        // `isLoading` true there would skeleton a table that is never coming.
        isLoading: canRead && (query.isLoading || allRows === null),
        error,
        onRetry,
        rows,
        totalCount: allRows?.length ?? 0,
        search,
        onSearchChange: setSearch,
        statusFilter,
        onStatusFilterChange: setStatusFilter,
        emptyState,
        onEdit,
        previewRow,
        onPreview,
        onPreviewOpenChange,
        onOpenHistory,
        // Not resolved against the loaded rows: a history link pasted into a
        // support thread has to open on a key this catalog may not carry (a
        // blocked row hidden by the filter, a template renamed since), and the
        // drawer states a failed read itself.
        historyKey: searchParams.get('history'),
        onCloseHistory,
        onSetBlocked: canEdit ? onSetBlocked : null,
        onCreate: canEdit ? onCreate : null,
        onManageCategories,
    };
};
